// One of the 5 placement tiles (left chest / centre / right chest / l-sleeve /
// r-sleeve etc). Drives from a `Placement` row in /api/config: name comes
// from admin, hint synthesised from view + free state.

import type { Placement } from "../types";

export function PlacementCard({
  placement,
  selected,
  disabled,
  disabledReason,
  onClick,
}: {
  placement: Placement;
  selected: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) {
  const hint = hintFor(placement);
  // Lower-tier UX when this card can't be picked (another spot already locks
  // it). Tooltip explains why so the customer doesn't think it's broken.
  const title = disabled && disabledReason ? `Conflicts with ${disabledReason}` : undefined;
  return (
    <button
      type="button"
      className={
        "rk-pc-pl-card" +
        (selected ? " is-selected" : "") +
        (disabled ? " is-disabled" : "")
      }
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      title={title}
    >
      {/* When the admin has uploaded a real photo for this placement on
          the template, show it instead of the abstract pip icon. Falls
          back to the SVG outline if no photo is set. */}
      {placement.imageUrl ? (
        <img
          src={placement.imageUrl}
          alt={placement.name}
          className="rk-pc-pl-card__photo"
          loading="lazy"
        />
      ) : (
        <PlacementIcon slug={placement.slug} view={placement.view} />
      )}
      <span className="rk-pc-pl-card__title">{placement.name}</span>
      {disabled && disabledReason ? (
        <span className="rk-pc-pl-card__hint">Locked by {disabledReason}</span>
      ) : hint ? (
        <span className="rk-pc-pl-card__hint">{hint}</span>
      ) : null}
      <span className="rk-pc-pl-card__max">
        {(() => {
          // freeEligible comes from the GlobalPlacement row and knows nothing
          // about tiers, so it kept saying "Free" on the FD bundle's centre
          // after its ladder was re-priced (2026-07-24, Oscar: the drawing
          // "shouldnt be free to have it at 300mm"). When this placement
          // carries explicit tiers with a paid band, say where free ends.
          // Placements without a paid explicit tier keep the old copy
          // verbatim, so no other product's card changes.
          const tiers = placement.tiers ?? [];
          if (tiers.length > 0) {
            // Explicit tiers are authoritative in BOTH directions: they also
            // fix the FD bundle's left-chest, whose GlobalPlacement says
            // freeEligible=false so the card claimed "Paid" over an explicit
            // all-free tier.
            const hasPaid = tiers.some((t) => t.feeCents > 0);
            if (!hasPaid) return `Free · up to ${placement.maxWidthMm}mm`;
            const freeTo = tiers
              .filter((t) => t.feeCents === 0)
              .reduce((m, t) => Math.max(m, t.upToMm), 0);
            return freeTo > 0
              ? `Free to ${freeTo}mm · up to ${placement.maxWidthMm}mm`
              : `Paid · up to ${placement.maxWidthMm}mm`;
          }
          return `${placement.freeEligible ? "Free" : "Paid"} · up to ${placement.maxWidthMm}mm`;
        })()}
      </span>
    </button>
  );
}

function hintFor(p: Placement): string {
  // Quick human hints based on slug or fallback to the view name. The admin
  // can override by encoding hints in the placement `name` itself.
  const s = p.slug.toLowerCase();
  if (s.includes("l-chest") || s.includes("left_chest") || s.includes("left-chest")) return "Heart side";
  if (s.includes("r-chest") || s.includes("right_chest") || s.includes("right-chest")) return "Above pocket";
  if (s.includes("centre") || s.includes("center")) return "Front centre";
  if (s.includes("sleeve")) return "Upper arm";
  if (s.includes("nape")) return "Back neck";
  if (s.includes("back")) return "Full back";
  return "";
}

// Tiny SVG icon. Renders a shirt outline with the matching spot dotted in
// hot pink so the customer can see what they're picking. Mirrors the design's
// shirt-with-pink-dot pattern.
function PlacementIcon({ slug, view }: { slug: string; view: string }) {
  const s = slug.toLowerCase();
  const isLeftChest = s.includes("l-chest") || s.includes("left_chest") || s.includes("left-chest");
  const isRightChest = s.includes("r-chest") || s.includes("right_chest") || s.includes("right-chest");
  const isCentre = s.includes("centre") || s.includes("center");
  const isLeftSleeve = s.includes("l-sleeve") || s.includes("left_sleeve") || s.includes("left-sleeve");
  const isRightSleeve = s.includes("r-sleeve") || s.includes("right_sleeve") || s.includes("right-sleeve");
  const isBack = view === "back" || s.includes("back");

  // Dot coords are MIRRORED (apparel convention: viewer's right is the
  // wearer's left chest).
  let dot: { x: number; y: number; r: number } | null = null;
  if (isLeftChest) dot = { x: 28, y: 22, r: 3 };
  else if (isRightChest) dot = { x: 16, y: 22, r: 3 };
  else if (isCentre) dot = { x: 22, y: 24, r: 5 };
  else if (isLeftSleeve) dot = { x: 38, y: 22, r: 3 };
  else if (isRightSleeve) dot = { x: 6, y: 22, r: 3 };
  else if (isBack) dot = { x: 22, y: 22, r: 5 };

  return (
    <svg className="rk-pc-pl-card__icon" viewBox="0 0 44 44" fill="none">
      {/* shirt silhouette */}
      <path
        d="M14 8 L8 12 L4 18 L8 22 L12 18 L12 38 L32 38 L32 18 L36 22 L40 18 L36 12 L30 8 Q22 13 14 8 Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="white"
      />
      {dot && <circle cx={dot.x} cy={dot.y} r={dot.r} fill="#f38bb9" stroke="#5c1756" strokeWidth="1" />}
    </svg>
  );
}
